import { useCallback, useEffect, useRef } from 'react';
import { TRANSITION_TIME } from 'data/global';

const SCROLLBAR_CLASS = 'is-scrolling';

const useScrollingClass = () => {
  const lastHoveredScrollbar = useRef(null);
  const scrollTimers = useRef(
    new WeakMap<Element, ReturnType<typeof setTimeout>>(),
  );

  const handleScroll = useCallback((e: Event) => {
    const target =
      e.target === document ? document.documentElement : (e.target as Element);

    target.classList.add(SCROLLBAR_CLASS);

    clearTimeout(scrollTimers.current.get(target));
    scrollTimers.current.set(
      target,
      setTimeout(() => {
        if (lastHoveredScrollbar.current !== target) {
          target.classList.remove(SCROLLBAR_CLASS);
        }
      }, TRANSITION_TIME),
    );
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const target = e.target as Element;

    if (!target) return;

    const overVerticalScrollbar =
      target.scrollHeight > target.clientHeight &&
      e.offsetX > target.clientWidth;

    const overHorizontalScrollbar =
      target.scrollWidth > target.clientWidth &&
      e.offsetY > target.clientHeight;

    const overScrollbar = overVerticalScrollbar || overHorizontalScrollbar;

    if (overScrollbar) {
      if (
        lastHoveredScrollbar.current &&
        lastHoveredScrollbar.current !== target
      )
        lastHoveredScrollbar.current.classList.remove(SCROLLBAR_CLASS);

      target.classList.add(SCROLLBAR_CLASS);
      lastHoveredScrollbar.current = target;

      return;
    }

    if (lastHoveredScrollbar.current) {
      lastHoveredScrollbar.current.classList.remove(SCROLLBAR_CLASS);
      lastHoveredScrollbar.current = null;
    }
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', handleScroll, true);
    document.addEventListener('mousemove', handleMouseMove, true);

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousemove', handleMouseMove, true);
    };
  }, [handleScroll, handleMouseMove]);
};

export default useScrollingClass;
